import React, { useState, useEffect, useMemo } from 'react';
import { DIAGNOSIS_OPTIONS, CYP_DIAGNOSIS_ADDITIONS, migrateDiagnosisValue } from './AssessmentTab';
import '../AssessmentForm.css';
import { useTreatmentPathwayOptions } from '../useTreatmentPathwayOptions';

/**
 * ProblemDescriptorTab
 *
 * Handles Problem Descriptor section for MH triage assessments (ReportTypeID 2002: Triage)
 * Maps to TriageInjuryDetails and Report table fields
 * This is a per-injury tab (TabPerInjury: TRUE)
 */

/**
 * Convert DateOnly object {Year, Month, Day} to YYYY-MM-DD string format
 */
const dateOnlyToString = (dateOnly) => {
  if (!dateOnly || !dateOnly.Year) return '';
  const year = dateOnly.Year;
  const month = String(dateOnly.Month).padStart(2, '0');
  const day = String(dateOnly.Day).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Convert YYYY-MM-DD string to DateOnly object {Year, Month, Day}
 */
const stringToDateOnly = (dateString) => {
  if (!dateString) return null;
  const parts = dateString.split('-');
  if (parts.length !== 3) return null;
  return {
    Year: parseInt(parts[0], 10),
    Month: parseInt(parts[1], 10),
    Day: parseInt(parts[2], 10)
  };
};

/**
 * Convert date-time string to YYYY-MM-DD format for date input
 * Handles both "2026-01-29T00:00:00" and DateOnly object formats
 */
const dateTimeToDateString = (dateValue) => {
  if (!dateValue) return '';
  // If it's already a DateOnly object, use dateOnlyToString
  if (typeof dateValue === 'object' && dateValue.Year) {
    return dateOnlyToString(dateValue);
  }
  // If it's a date-time string, extract just the date part
  if (typeof dateValue === 'string') {
    return dateValue.split('T')[0];
  }
  return '';
};

const ProblemDescriptorTab = ({ reportId, api, patientInfo, onSave , isPublished}) => {
  // Form state
  const [primaryDiagnosis, setPrimaryDiagnosis] = useState('');
  const [primaryDiagnosisOther, setPrimaryDiagnosisOther] = useState('');
  const [secondaryDiagnosis, setSecondaryDiagnosis] = useState('');
  const [secondaryDiagnosisOther, setSecondaryDiagnosisOther] = useState('');
  const [treatmentPathwayId, setTreatmentPathwayId] = useState('');
  const [clinicalRationale, setClinicalRationale] = useState('');
  const [virtualAssessmentRequired, setVirtualAssessmentRequired] = useState(null);
  const [virtualAssessmentDue, setVirtualAssessmentDue] = useState('');
  const [physiotherapyReferralRequired, setPhysiotherapyReferralRequired] = useState(null);
  const [preferredTreatingClinicianGender, setPreferredTreatingClinicianGender] = useState('');
  const [noAssessments, setNoAssessments] = useState('');
  const [noTreatments, setNoTreatments] = useState('');
  const [summaryForAP, setSummaryForAP] = useState('');
  const [patientSummary, setPatientSummary] = useState('');
  const [signedBy, setSignedBy] = useState('');
  const [regNo, setRegNo] = useState('');
  const [signedDate, setSignedDate] = useState('');

  // Dropdown options
  const diagnosisOptions = useMemo(() => {
    if (patientInfo?.isCYPApplicable) {
      const combined = [...DIAGNOSIS_OPTIONS, ...CYP_DIAGNOSIS_ADDITIONS];
      return combined.sort((a, b) => a.value.localeCompare(b.value));
    }
    return DIAGNOSIS_OPTIONS;
  }, [patientInfo]);
  const { options: treatmentPathwayOptions } = useTreatmentPathwayOptions(
    api,
    reportId,
    patientInfo?.isCYPApplicable
  );
  const [genderOptions] = useState([
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: 'N', label: 'No Preference' }
  ]);
  const [assessmentOptions] = useState([
    { value: '0', label: '0' },
    { value: '1', label: '1' }
  ]);
  const [treatmentCountOptions] = useState([
    { value: '0', label: '0' },
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10', label: '10' },
    { value: '11', label: '11' },
    { value: '12', label: '12' },
    { value: '13', label: '13' },
    { value: '14', label: '14' },
    { value: '15', label: '15' },
    { value: '16', label: '16' }
  ]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  useEffect(() => {
    loadProblemDescriptorData();
  }, [reportId]);

  const loadProblemDescriptorData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-problem-descriptor`);

      if (response.ok) {
        const data = await response.json();
        setPrimaryDiagnosis(migrateDiagnosisValue(data.primaryDiagnosis || ''));
        setPrimaryDiagnosisOther(data.primaryDiagnosisOther || '');
        setSecondaryDiagnosis(migrateDiagnosisValue(data.secondaryDiagnosis || ''));
        setSecondaryDiagnosisOther(data.secondaryDiagnosisOther || '');
        setTreatmentPathwayId(data.treatmentPathwayID?.toString() || '');
        setClinicalRationale(data.clinicalRationale || '');
        setVirtualAssessmentRequired(data.virtualAssessmentRequired);
        setVirtualAssessmentDue(dateTimeToDateString(data.virtualAssessmentDue));
        setPhysiotherapyReferralRequired(data.physiotherapyReferralRequired);
        setPreferredTreatingClinicianGender(data.preferredTreatingClinicianGender || '');
        setNoAssessments(data.noAssessments?.toString() || '');
        setNoTreatments(data.noTreatments?.toString() || '');
        setSummaryForAP(data.summaryForAP || '');
        setPatientSummary(data.patientSummary || '');
        setSignedBy(data.signedBy || '');
        setRegNo(data.regNo || '');
        setSignedDate(dateTimeToDateString(data.signedDate));

        // Set loaded fields as 'saved'
        const initialSavingStates = {};
        if (data.primaryDiagnosis) initialSavingStates['PrimaryDiagnosis'] = 'saved';
        if (data.primaryDiagnosisOther) initialSavingStates['PrimaryDiagnosisOther'] = 'saved';
        if (data.secondaryDiagnosis) initialSavingStates['SecondaryDiagnosis'] = 'saved';
        if (data.secondaryDiagnosisOther) initialSavingStates['SecondaryDiagnosisOther'] = 'saved';
        if (data.treatmentPathwayID) initialSavingStates['TreatmentPathwayId'] = 'saved';
        if (data.clinicalRationale) initialSavingStates['ClinicalRationale'] = 'saved';
        if (data.virtualAssessmentRequired !== null) initialSavingStates['VirtualAssessmentRequired'] = 'saved';
        if (data.virtualAssessmentDue) initialSavingStates['VirtualAssessmentDue'] = 'saved';
        if (data.physiotherapyReferralRequired !== null) initialSavingStates['PhysiotherapyReferralRequired'] = 'saved';
        if (data.preferredTreatingClinicianGender) initialSavingStates['PreferredTreatingClinicianGender'] = 'saved';
        if (data.noAssessments) initialSavingStates['NoAssessments'] = 'saved';
        if (data.noTreatments) initialSavingStates['NoTreatments'] = 'saved';
        if (data.summaryForAP) initialSavingStates['SummaryForAp'] = 'saved';
        if (data.patientSummary) initialSavingStates['PatientSummary'] = 'saved';
        if (data.signedDate) initialSavingStates['SignedDate'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading problem descriptor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-problem-descriptor/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    const fieldValues = {
      'PrimaryDiagnosis': primaryDiagnosis,
      'PrimaryDiagnosisOther': primaryDiagnosisOther,
      'SecondaryDiagnosis': secondaryDiagnosis,
      'SecondaryDiagnosisOther': secondaryDiagnosisOther,
      'TreatmentPathwayId': treatmentPathwayId,
      'ClinicalRationale': clinicalRationale,
      'VirtualAssessmentRequired': virtualAssessmentRequired,
      'VirtualAssessmentDue': virtualAssessmentDue,
      'PhysiotherapyReferralRequired': physiotherapyReferralRequired,
      'PreferredTreatingClinicianGender': preferredTreatingClinicianGender,
      'NoAssessments': noAssessments,
      'NoTreatments': noTreatments,
      'SummaryForAp': summaryForAP,
      'PatientSummary': patientSummary,
      'SignedDate': signedDate
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    saveField(fieldName, value);
  };

  const handleBooleanChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value);
  };

  const handleDropdownChange = (fieldName, value, setter) => {
    setter(value);
    saveField(fieldName, value === '' ? null : value);
  };

  const handleDateChange = (fieldName, value, setter) => {
    setter(value);
    // Both SignedDate and VirtualAssessmentDue expect date-time string format "YYYY-MM-DDT00:00:00"
    const dateTimeValue = value ? `${value}T00:00:00` : null;
    saveField(fieldName, dateTimeValue);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading problem descriptor...</p>
      </div>
    );
  }

  const showPrimaryDiagnosisOther = primaryDiagnosis === 'Other';
  const showSecondaryDiagnosisOther = secondaryDiagnosis === 'Other';

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="patient-summary">
        <div><strong>Case ID:</strong> {patientInfo?.caseId}</div>
        <div><strong>Name:</strong> {patientInfo?.name}</div>
        <div><strong>DOB:</strong> {patientInfo?.dateOfBirth}</div>
        <div><strong>Address:</strong> {patientInfo?.address}</div>
      </div>

      <div className="form-section">
        <h3>Diagnosis</h3>

        <div className="form-group">
          <label htmlFor="primaryDiagnosis">
            Primary Diagnosis: <span className="required">*</span>
            {getSaveIndicator('PrimaryDiagnosis')}
          </label>
          <select
            id="primaryDiagnosis"
            value={primaryDiagnosis}
            onChange={(e) => handleDropdownChange('PrimaryDiagnosis', e.target.value, setPrimaryDiagnosis)}
            style={{ width: '100%' }}
            required
          >
            <option value="">-- Select --</option>
            {diagnosisOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
            <option value="Other">Other</option>
          </select>
        </div>

        {showPrimaryDiagnosisOther && (
          <div className="form-group">
            <label htmlFor="primaryDiagnosisOther">
              Please state:
              {getSaveIndicator('PrimaryDiagnosisOther')}
            </label>
            <input
              type="text"
              id="primaryDiagnosisOther"
              value={primaryDiagnosisOther}
              onChange={(e) => handleFieldChange('PrimaryDiagnosisOther', e.target.value, setPrimaryDiagnosisOther)}
              onBlur={() => handleFieldBlur('PrimaryDiagnosisOther', primaryDiagnosisOther)}
              style={{ width: '100%' }}
            />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="secondaryDiagnosis">
            Secondary Diagnosis:
            {getSaveIndicator('SecondaryDiagnosis')}
          </label>
          <select
            id="secondaryDiagnosis"
            value={secondaryDiagnosis}
            onChange={(e) => handleDropdownChange('SecondaryDiagnosis', e.target.value, setSecondaryDiagnosis)}
            style={{ width: '100%' }}
          >
            <option value="">-- Select --</option>
            {diagnosisOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
            <option value="Other">Other</option>
          </select>
        </div>

        {showSecondaryDiagnosisOther && (
          <div className="form-group">
            <label htmlFor="secondaryDiagnosisOther">
              Please state:
              {getSaveIndicator('SecondaryDiagnosisOther')}
            </label>
            <input
              type="text"
              id="secondaryDiagnosisOther"
              value={secondaryDiagnosisOther}
              onChange={(e) => handleFieldChange('SecondaryDiagnosisOther', e.target.value, setSecondaryDiagnosisOther)}
              onBlur={() => handleFieldBlur('SecondaryDiagnosisOther', secondaryDiagnosisOther)}
              style={{ width: '100%' }}
            />
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Treatment Pathway</h3>

        <div className="form-group">
          <label htmlFor="treatmentPathwayId">
            Treatment Pathway: <span className="required">*</span>
            {getSaveIndicator('TreatmentPathwayId')}
          </label>
          <select
            id="treatmentPathwayId"
            value={treatmentPathwayId}
            onChange={(e) => handleDropdownChange('TreatmentPathwayId', e.target.value, setTreatmentPathwayId)}
            style={{ width: '100%' }}
            required
          >
            <option value="">-- Select --</option>
            {treatmentPathwayOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="clinicalRationale">
            Clinical Rationale:
            {getSaveIndicator('ClinicalRationale')}
          </label>
          <textarea
            id="clinicalRationale"
            value={clinicalRationale}
            onChange={(e) => handleFieldChange('ClinicalRationale', e.target.value, setClinicalRationale)}
            onBlur={() => handleFieldBlur('ClinicalRationale', clinicalRationale)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Assessment Requirements</h3>

        <div className="form-group">
          <label className="inline-label">
            <strong>Is virtual treatment required?</strong>
            {getSaveIndicator('VirtualAssessmentRequired')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="virtualAssessmentRequired"
                checked={virtualAssessmentRequired === true}
                onChange={() => handleBooleanChange('VirtualAssessmentRequired', true, setVirtualAssessmentRequired)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="virtualAssessmentRequired"
                checked={virtualAssessmentRequired === false}
                onChange={() => handleBooleanChange('VirtualAssessmentRequired', false, setVirtualAssessmentRequired)}
              />
              No
            </label>
          </div>
        </div>

        {virtualAssessmentRequired === true && (
          <div className="form-group">
            <label htmlFor="virtualAssessmentDue">
              When is the virtual assessment due?
              {getSaveIndicator('VirtualAssessmentDue')}
            </label>
            <input
              type="date"
              id="virtualAssessmentDue"
              value={virtualAssessmentDue}
              onChange={(e) => handleDateChange('VirtualAssessmentDue', e.target.value, setVirtualAssessmentDue)}
              style={{ width: '250px' }}
            />
          </div>
        )}

        <div className="form-group">
          <label className="inline-label">
            <strong>Is a network referral required?</strong>
            {getSaveIndicator('PhysiotherapyReferralRequired')}
          </label>
          <div className="radio-group-inline">
            <label>
              <input
                type="radio"
                name="physiotherapyReferralRequired"
                checked={physiotherapyReferralRequired === true}
                onChange={() => handleBooleanChange('PhysiotherapyReferralRequired', true, setPhysiotherapyReferralRequired)}
              />
              Yes
            </label>
            <label>
              <input
                type="radio"
                name="physiotherapyReferralRequired"
                checked={physiotherapyReferralRequired === false}
                onChange={() => handleBooleanChange('PhysiotherapyReferralRequired', false, setPhysiotherapyReferralRequired)}
              />
              No
            </label>
          </div>
        </div>

        {physiotherapyReferralRequired === true && (
          <div className="form-group">
            <label htmlFor="preferredTreatingClinicianGender">
              Preferred treating clinician gender: <span className="required">*</span>
              {getSaveIndicator('PreferredTreatingClinicianGender')}
            </label>
            <select
              id="preferredTreatingClinicianGender"
              value={preferredTreatingClinicianGender}
              onChange={(e) => handleDropdownChange('PreferredTreatingClinicianGender', e.target.value, setPreferredTreatingClinicianGender)}
              style={{ width: '250px' }}
              required
            >
              <option value="">-- Select --</option>
              {genderOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="noAssessments">
            Assessments:
            {getSaveIndicator('NoAssessments')}
          </label>
          <select
            id="noAssessments"
            value={noAssessments}
            onChange={(e) => handleDropdownChange('NoAssessments', e.target.value, setNoAssessments)}
            style={{ width: '150px' }}
          >
            <option value="">-- Select --</option>
            {assessmentOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="noTreatments">
            Treatments: <span className="required">*</span>
            {getSaveIndicator('NoTreatments')}
          </label>
          <select
            id="noTreatments"
            value={noTreatments}
            onChange={(e) => handleDropdownChange('NoTreatments', e.target.value, setNoTreatments)}
            style={{ width: '150px' }}
            required
          >
            <option value="">-- Select --</option>
            {treatmentCountOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-section">
        <h3>Summary Notes</h3>

        <div className="form-group">
          <label htmlFor="summaryForAP">
            Notes for clinic:
            {getSaveIndicator('SummaryForAp')}
          </label>
          <textarea
            id="summaryForAP"
            value={summaryForAP}
            onChange={(e) => handleFieldChange('SummaryForAp', e.target.value, setSummaryForAP)}
            onBlur={() => handleFieldBlur('SummaryForAp', summaryForAP)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="patientSummary">
            Patient Summary:
            {getSaveIndicator('PatientSummary')}
          </label>
          <textarea
            id="patientSummary"
            value={patientSummary}
            onChange={(e) => handleFieldChange('PatientSummary', e.target.value, setPatientSummary)}
            onBlur={() => handleFieldBlur('PatientSummary', patientSummary)}
            rows="4"
            style={{ width: '100%' }}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Triage Completion</h3>

        <div className="form-group">
          <label>
            <strong>Triage call completed by:</strong>
          </label>
          <div className="readonly-field">{signedBy || 'Not specified'}</div>
        </div>

        <div className="form-group">
          <label>
            <strong>Registration number of clinician:</strong>
          </label>
          <div className="readonly-field">{regNo || 'Not specified'}</div>
        </div>

        <div className="form-group">
          <label htmlFor="signedDate">
            Triage call date: <span className="required">*</span>
            {getSaveIndicator('SignedDate')}
          </label>
          <input
            type="date"
            id="signedDate"
            value={signedDate}
            onChange={(e) => handleDateChange('SignedDate', e.target.value, setSignedDate)}
            style={{ width: '250px' }}
            required
          />
        </div>
      </div>
      </fieldset>

    </div>
  );
};

export default ProblemDescriptorTab;
